import React, { useState, useMemo } from 'react'
import Modal from '../Modal'
import { useTranslation } from 'react-i18next'
import { BetTypeEnum } from '../../BetLotto/BetLotto.types'
import Pagination from '../../Pagination/Pagination'

const headerMap = {
  limit: 'numberLimitModal.header.limit',
  restricted: 'numberLimitModal.header.restricted',
}

const ITEMS_PER_PAGE = 10

export interface NumberLimitItem {
  type: BetTypeEnum | string
  number: string
  amount: number
}

export interface NumberLimitModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  onCancel: () => void
  data: NumberLimitItem[]
  type: 'limit' | 'restricted'
  itemsPerPage?: number
}

const NumberLimitModal: React.FC<NumberLimitModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  data,
  type,
  itemsPerPage = ITEMS_PER_PAGE,
}) => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(1)

  // Frontend pagination logic
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(data.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedData = data.slice(startIndex, endIndex)

    return {
      totalPages,
      paginatedData,
      startIndex,
      endIndex,
      totalItems: data.length,
    }
  }, [data, currentPage, itemsPerPage])

  // Reset to first page if data changes and currentPage is out of range
  React.useEffect(() => {
    if (
      currentPage > paginationData.totalPages &&
      paginationData.totalPages > 0
    ) {
      setCurrentPage(1)
    }
  }, [data, paginationData.totalPages, currentPage])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= paginationData.totalPages) {
      setCurrentPage(page)
    }
  }

  const renderPagination = () => {
    if (paginationData.totalPages <= 1) return null

    const { totalPages } = paginationData

    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t(headerMap[type])}: ${data.length}`}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-6 py-2 bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border border-[var(--bet-type-border)] hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)] rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="px-6 py-2 active-gradient font-medium rounded-lg hover:opacity-90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            onClick={onConfirm}
          >
            {t('common.confirm')}
          </button>
        </div>
      }
      modalContainerStyle={{
        maxWidth: 480,
        width: '100%',
        maxHeight: '80vh',
        margin: '0 auto',
      }}
    >
      <div className="space-y-3">
        <div className="flex justify-between items-center border-b border-[var(--bet-type-hover-text)] pb-2 mb-2">
          <span className="w-1/3 text-[var(--bet-type-hover-text)] font-semibold text-xs sm:text-sm text-left">
            {t('numberLimitModal.typeOfBet')}
          </span>
          <span className="w-1/3 text-[var(--bet-type-hover-text)] font-semibold text-xs sm:text-sm text-center">
            {t('numberLimitModal.numberHitLimit')}
          </span>
          <span className="w-1/3 text-[var(--bet-type-hover-text)] font-semibold text-xs sm:text-sm text-right">
            {t('numberLimitModal.amountPaid')}
          </span>
        </div>

        <div className="space-y-4">
          {paginationData.paginatedData.map(
            (item: NumberLimitItem, index: number) => (
              <div
                key={`${item.type}-${item.number}-${paginationData.startIndex + index}`}
                className="flex justify-between items-center w-full py-2"
              >
                <span className="w-1/3 text-[var(--card-subtext)] text-xs sm:text-sm text-left">
                  {item.type}
                </span>
                <span className="w-1/3 text-[var(--card-subtext)] text-xs sm:text-sm text-center">
                  {item.number}
                </span>
                <span className="w-1/3 text-[var(--card-subtext)] text-xs sm:text-sm text-right">
                  {item.amount}
                </span>
              </div>
            ),
          )}
        </div>

        {renderPagination()}
      </div>
    </Modal>
  )
}

export default NumberLimitModal
